package org.teddapp.project.ui

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/**
 * Logo TEDD : carré arrondi en dégradé, anneau de progression aux trois quarts, T blanc.
 *
 * C'est la marque, pas un élément du thème : elle garde ses couleurs d'origine et son
 * dégradé, seule exception à la règle « pas de dégradé » du design system.
 * Le dessin est sur une grille de 200 × 200, mise à l'échelle de la taille demandée.
 */
@Composable
fun TeddLogo(size: Dp = 28.dp, modifier: Modifier = Modifier) {
    val logoGradient = TeddTokens.LogoGradient
    val arcGradient = TeddTokens.LogoArcGradient
    Canvas(modifier = modifier.size(size)) {
        val s = this.size.minDimension
        val scale = s / 200f

        // 1) Rounded square 10..190 (180×180), corner radius 44
        val rectInset = 10f * scale
        val rectSize = 180f * scale
        val cornerR = 44f * scale
        drawRoundRect(
            brush = logoGradient,
            topLeft = Offset(rectInset, rectInset),
            size = Size(rectSize, rectSize),
            cornerRadius = CornerRadius(cornerR, cornerR),
        )

        // 2) Track circle (white @ 22%) — radius 55, stroke 10
        val center = Offset(100f * scale, 100f * scale)
        val circleR = 55f * scale
        val strokeW = 10f * scale
        drawCircle(
            color = Color.White.copy(alpha = 0.22f),
            radius = circleR,
            center = center,
            style = Stroke(width = strokeW, cap = StrokeCap.Round),
        )

        // 3) Progress arc — same circle, but only ~75% (260 of 346 perimeter).
        // SVG: strokeDasharray="260 346" + rotate(-90 100 100) → arc starts at top (12 o'clock),
        // sweeps clockwise for 260/346 ≈ 75% = 270°.
        val sweepDeg = 260f / 346f * 360f
        drawArc(
            brush = arcGradient,
            startAngle = -90f,
            sweepAngle = sweepDeg,
            useCenter = false,
            topLeft = Offset(center.x - circleR, center.y - circleR),
            size = Size(circleR * 2, circleR * 2),
            style = Stroke(width = strokeW, cap = StrokeCap.Round),
        )

        // 4) T shape (white) — two rounded rects
        // Horizontal bar: x=60 y=70 w=80 h=16, rx=8
        drawRoundRect(
            color = Color.White,
            topLeft = Offset(60f * scale, 70f * scale),
            size = Size(80f * scale, 16f * scale),
            cornerRadius = CornerRadius(8f * scale, 8f * scale),
        )
        // Vertical bar: x=92 y=70 w=16 h=65, rx=8
        drawRoundRect(
            color = Color.White,
            topLeft = Offset(92f * scale, 70f * scale),
            size = Size(16f * scale, 65f * scale),
            cornerRadius = CornerRadius(8f * scale, 8f * scale),
        )
    }
}
